import type { AsOpStatus } from '../../../components/base/as-op-status.ts';
import type { ExportStatusDetail } from '../../../data/data-interfaces.ts';
import type { ExportBuilder } from '../lib/exportBuilder.ts';

/** Tooltip shown on download buttons disabled via internalData.canDownload. */
export const DOWNLOAD_DISABLED_TOOLTIP = 'This app needs a license for downloads.';
/** Localization key for DOWNLOAD_DISABLED_TOOLTIP. */
export const DOWNLOAD_DISABLED_TOOLTIP_DICT_KEY = 'fileIO.download.disabledTooltip';
/** Localization key for the default 'Downloading...' pending label. */
export const DOWNLOAD_PENDING_DICT_KEY = 'fileIO.download.pending';

/**
 * Wires a plain <as-button> + sibling <as-op-status> pair up as a download
 * action (replaces the old <as-download-button> component):
 *   - as-button-click on the button starts exporter.download() on a microtask,
 *     so every listener up the tree can still configure the exporter first
 *   - as-export-status events (operation 'download', matching exporterId) drive
 *     the op-status and swap the button into a disabled pending state
 *   - as-custom-data-changed resets the status back to idle
 */
export interface DownloadWiringOptions {
  /** The <as-button> that triggers the download. */
  button: HTMLElement;
  /** Sibling <as-op-status>; resolved lazily so re-rendering parents stay correct. */
  status: () => AsOpStatus | null;
  /** Exporter to download from; resolved at click time. */
  getExporter: () => ExportBuilder | null;
  /** Download filename (including extension); resolved at click time. */
  getFilename?: () => string | undefined;
  /** exporterId filter for as-export-status events (default: 'default'). */
  getExporterId?: () => string;
  /** Button label while the download runs (default: 'Downloading...'). */
  getPendingLabel?: () => { defaultText: string; dictKey?: string | null };
}

export interface DownloadWiring {
  /** Back to idle: restores the button label/disabled state and clears the op-status. */
  reset(): void;
  /** Detach all listeners. */
  dispose(): void;
}

export function wireDownloadButton(opts: DownloadWiringOptions): DownloadWiring {
  const { button } = opts;
  let pending = false;
  let idleText: string | null = null;
  let idleDictKey: string | null = null;
  let idleDisabled = false;

  const setPending = (on: boolean): void => {
    if (on === pending) return;
    pending = on;
    if (on) {
      idleText     = button.getAttribute('default-text');
      idleDictKey  = button.getAttribute('dict-key');
      idleDisabled = button.hasAttribute('disabled');
      const label = opts.getPendingLabel?.() ?? { defaultText: 'Downloading...', dictKey: DOWNLOAD_PENDING_DICT_KEY };
      button.setAttribute('default-text', label.defaultText);
      if (label.dictKey) button.setAttribute('dict-key', label.dictKey);
      else button.removeAttribute('dict-key');
      button.setAttribute('disabled', '');
    } else {
      if (idleText != null) button.setAttribute('default-text', idleText);
      if (idleDictKey != null) button.setAttribute('dict-key', idleDictKey);
      else button.removeAttribute('dict-key');
      if (!idleDisabled) button.removeAttribute('disabled');
    }
  };

  const onStatus = (e: Event): void => {
    const { exporterId, operation, state, message } = (e as CustomEvent<ExportStatusDetail>).detail;
    if (operation !== 'download') return;
    if (exporterId !== (opts.getExporterId?.() ?? 'default')) return;

    setPending(state === 'pending');
    const status = opts.status();
    switch (state) {
      case 'pending':   status?.setPending(); break;
      case 'success':   status?.setSuccess(message); break;
      case 'error':     status?.setError(message); break;
      // A cancel isn't a failure — drop straight back to idle.
      case 'cancelled': status?.reset(); break;
    }
  };

  const onClick = (): void => {
    // Microtask: as-button-click is still propagating when this listener runs —
    // defer so every listener up the tree can configure the exporter before the
    // download starts (the old component dispatched the event itself and only
    // downloaded after dispatchEvent returned).
    queueMicrotask(() => {
      opts.getExporter()?.download({ filename: opts.getFilename?.() || undefined })
        .catch(() => { /* error state shown via as-export-status */ });
    });
  };

  const reset = (): void => {
    setPending(false);
    opts.status()?.reset();
  };

  button.addEventListener('as-button-click', onClick);
  window.addEventListener('as-export-status', onStatus);
  window.addEventListener('as-custom-data-changed', reset);

  return {
    reset,
    dispose(): void {
      button.removeEventListener('as-button-click', onClick);
      window.removeEventListener('as-export-status', onStatus);
      window.removeEventListener('as-custom-data-changed', reset);
    },
  };
}
